import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './shell/app-shell.js';
import { RouteGuard } from './session/route-guard.js';
import { LoginPage } from './pages/login-page.js';
import { DashboardPage } from './pages/dashboard-page.js';
import { MaintenancePage } from './pages/maintenance-page.js';
import { UserInspectPage } from './pages/user-inspect-page.js';
import { OrderInspectPage } from './pages/order-inspect-page.js';
import { ProviderControlPlanePage } from './pages/provider-control-plane-page.js';
import { GmxPage } from './pages/gmx/GmxPage.js';
import { RamblerPage } from './pages/rambler/RamblerPage.js';
import { GmxboxPage } from './pages/gmxbox/GmxboxPage.js';
import { AolPage } from './pages/aol/AolPage.js';
import { LiberoPage } from './pages/libero/LiberoPage.js';
import { TiscaliitPage } from './pages/tiscaliit/TiscaliitPage.js';
import { TiscaliczPage } from './pages/tiscalicz/TiscaliczPage.js';
import { SinavipPage } from './pages/sinavip/SinavipPage.js';
import { SinaPage } from './pages/sina/SinaPage.js';
import { GazetaplPage } from './pages/gazetapl/GazetaplPage.js';
import { RediffmailPage } from './pages/rediffmail/RediffmailPage.js';
import { TonlinePage } from './pages/tonline/TonlinePage.js';
import { RecentActivityPage } from './pages/recent-activity-page.js';
import { ObservabilityPage } from './pages/observability-page.js';
import { FinancialReviewsPage } from './pages/financial-reviews-page.js';
import { DomainPricingPage } from './pages/domain-pricing-page.js';
import { AnnouncementsPage } from './pages/announcements-page.js';
import { ChatInboxPage } from './pages/chat-inbox-page.js';
import { TierConfigPage } from './pages/tier-config-page.js';
import { AlertsPage } from './pages/alerts-page.js';
import { AcquisitionPage } from './pages/acquisition-page.js';

/**
 * SPA redirect landing — counterpart of the API's admin-spa-navigation
 * redirect hook (apps/api/src/routes/admin/admin-spa-navigation-redirect.ts).
 *
 * The live ingress routes /admin/users, /admin/orders, /admin/pricing, ...
 * (which are BOTH API prefixes and SPA page paths) to kopechka-api, so a
 * direct load / reload of such a URL returns the API's JSON. The API now
 * answers browser navigations with `302 /admin/?to=<original path+query>`;
 * this component re-enters the requested route client-side.
 *
 * Security: `to` is only honoured when it is a plain same-origin path under
 * `/admin/` — no `//`, no `:` in the path (scheme smuggling), no backslashes,
 * no `.`/`..` segments. Anything else is ignored and the app renders `/`.
 */
export const ADMIN_SPA_BASENAME = '/admin';
const SPA_REDIRECT_QUERY_KEY = 'to';
// RFC 3986 pchar minus ':' (rejects '//' via the +, '\\' and ':' outright).
const SAFE_SEGMENT = /^[A-Za-z0-9._~%!$&'()*+,;=@-]+$/;

/**
 * Validates the `?to=` value and returns the router-relative target (basename
 * stripped, query + hash preserved) or `null` when it must be ignored.
 */
export function resolveSpaRedirectTarget(to: string | null | undefined): string | null {
  if (typeof to !== 'string' || to.length === 0) return null;
  if (!to.startsWith(`${ADMIN_SPA_BASENAME}/`)) return null;
  if (to.includes('\\')) return null;
  for (let i = 0; i < to.length; i += 1) {
    const code = to.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) return null;
  }
  const hashStart = to.indexOf('#');
  const beforeHash = hashStart === -1 ? to : to.slice(0, hashStart);
  const queryStart = beforeHash.indexOf('?');
  const pathPart = queryStart === -1 ? beforeHash : beforeHash.slice(0, queryStart);
  const relativePath = pathPart.slice(ADMIN_SPA_BASENAME.length); // keeps leading '/'
  const segments = relativePath.split('/').slice(1); // drop the empty head before '/'
  // Allow a single trailing slash ("/admin/users/") but nothing else empty.
  if (segments.length > 0 && segments[segments.length - 1] === '') segments.pop();
  for (const segment of segments) {
    if (segment === '' || segment === '.' || segment === '..') return null;
    if (!SAFE_SEGMENT.test(segment)) return null;
    // Percent-encoded separators (%2F, %5C, %3A) or dots must not smuggle
    // a path/host/scheme past the raw-segment checks above.
    let decoded: string;
    try {
      decoded = decodeURIComponent(segment);
    } catch {
      return null;
    }
    if (decoded === '.' || decoded === '..') return null;
    if (decoded.includes('/') || decoded.includes('\\') || decoded.includes(':')) return null;
  }
  return relativePath + to.slice(pathPart.length);
}

/**
 * Must render INSIDE the BrowserRouter (basename="/admin"): when the router
 * location is the SPA entry ('/') carrying a valid `?to=`, it navigates there
 * with replace (cleaning the URL) BEFORE any route renders; otherwise it
 * renders its children untouched. The RouteGuard downstream still handles
 * the unauthenticated case (it stores `from=` and the login page returns to
 * it), so a cold reload on a phone lands on the intended page after login.
 */
export function SpaRedirectLanding({ children }: { readonly children: ReactNode }): JSX.Element {
  const location = useLocation();
  if (location.pathname === '/' || location.pathname === '') {
    const to = new URLSearchParams(location.search).get(SPA_REDIRECT_QUERY_KEY);
    if (to !== null) {
      const target = resolveSpaRedirectTarget(to);
      if (target !== null) {
        return <Navigate to={target} replace />;
      }
    }
  }
  return <>{children}</>;
}

export function App(): JSX.Element {
  return (
    <SpaRedirectLanding>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <RouteGuard>
              <AppShell />
            </RouteGuard>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route path="users/:actorId" element={<UserInspectPage />} />
          <Route path="users" element={<UserInspectPage />} />
          <Route path="messages" element={<ChatInboxPage />} />
          <Route path="orders/:orderId" element={<OrderInspectPage />} />
          <Route path="orders" element={<OrderInspectPage />} />
          <Route path="mailcom-control-plane" element={<ProviderControlPlanePage />} />
          {/* Phase 37 — alias path so /admin/providers/mailcom resolves to the
              same Parent Mailler dashboard. Both the operator-friendly URL and
              the resource-style URL point at one component. */}
          <Route path="providers/mailcom" element={<ProviderControlPlanePage />} />
          {/* D-228 GMX — SAME page stack bound to providerId='gmx'. Operator URL
              (/gmx-control-plane) + resource URL (/providers/gmx) both resolve to
              the gmx-bound wrapper. Every /providers/<id> route is a static
              segment; there is no dynamic /providers/:providerId route (the
              "Sağlayıcı Sağlığı" panel page was removed 2026-10-23), so an id
              without a static route falls through to the catch-all below. */}
          <Route path="gmx-control-plane" element={<GmxPage />} />
          <Route path="providers/gmx" element={<GmxPage />} />
          {/* D-247 (2026-10-06) — GMX Mailbox (provider id gmxbox, label "GMX"):
              the healthy GMX parents sold as mailboxes. Same single-page layout
              as the other mailbox providers (shared MailboxUnit dashboard +
              manager + the Import button, Q30). The old alias line stays on
              /gmx-control-plane (read-only, passive). */}
          <Route path="gmxbox-control-plane" element={<GmxboxPage />} />
          <Route path="gmxbox-control-plane/raw" element={<Navigate to="/gmxbox-control-plane" replace />} />
          <Route path="providers/gmxbox" element={<GmxboxPage />} />
          {/* R-RAMBLER (2026-09-15) — SAME page stack bound to
              providerId='rambler'. No alias system: the row shown per account is
              the mailbox itself, and the per-site sale rights live in
              alias_site_serves / alias_used_site_keys. */}
          <Route path="rambler-control-plane" element={<RamblerPage />} />
          {/* 2026-09-16 (operator): ONE rambler page — stock, probe state,
              per-site sales, per-mailbox detail and import all live on it. The
              former /raw mount folds back into the same page. */}
          <Route path="rambler-control-plane/raw" element={<Navigate to="/rambler-control-plane" replace />} />
          <Route path="providers/rambler" element={<RamblerPage />} />
          {/* R-AOL (2026-09-16) — mirrors rambler exactly: no alias system, the
              mailbox is the sellable unit, the sale right is per (mailbox, site).
              Same single-page layout (stock, probe state, per-site sales,
              per-mailbox detail, import). */}
          <Route path="aol-control-plane" element={<AolPage />} />
          <Route path="aol-control-plane/raw" element={<Navigate to="/aol-control-plane" replace />} />
          <Route path="providers/aol" element={<AolPage />} />
          {/* R-LIBERO (2026-09-16) — same single-page layout as the others. */}
          <Route path="libero-control-plane" element={<LiberoPage />} />
          <Route path="libero-control-plane/raw" element={<Navigate to="/libero-control-plane" replace />} />
          <Route path="providers/libero" element={<LiberoPage />} />
          <Route path="tiscaliit-control-plane" element={<TiscaliitPage />} />
          <Route path="tiscaliit-control-plane/raw" element={<Navigate to="/tiscaliit-control-plane" replace />} />
          <Route path="providers/tiscaliit" element={<TiscaliitPage />} />
          <Route path="tiscalicz-control-plane" element={<TiscaliczPage />} />
          <Route path="tiscalicz-control-plane/raw" element={<Navigate to="/tiscalicz-control-plane" replace />} />
          <Route path="providers/tiscalicz" element={<TiscaliczPage />} />
          <Route path="sinavip-control-plane" element={<SinavipPage />} />
          <Route path="sinavip-control-plane/raw" element={<Navigate to="/sinavip-control-plane" replace />} />
          <Route path="providers/sinavip" element={<SinavipPage />} />
          <Route path="sina-control-plane" element={<SinaPage />} />
          <Route path="sina-control-plane/raw" element={<Navigate to="/sina-control-plane" replace />} />
          <Route path="providers/sina" element={<SinaPage />} />
          <Route path="gazetapl-control-plane" element={<GazetaplPage />} />
          <Route path="gazetapl-control-plane/raw" element={<Navigate to="/gazetapl-control-plane" replace />} />
          <Route path="providers/gazetapl" element={<GazetaplPage />} />
          <Route path="rediffmail-control-plane" element={<RediffmailPage />} />
          <Route path="rediffmail-control-plane/raw" element={<Navigate to="/rediffmail-control-plane" replace />} />
          <Route path="providers/rediffmail" element={<RediffmailPage />} />
          <Route path="tonline-control-plane" element={<TonlinePage />} />
          <Route path="tonline-control-plane/raw" element={<Navigate to="/tonline-control-plane" replace />} />
          <Route path="providers/tonline" element={<TonlinePage />} />
          <Route path="activity" element={<RecentActivityPage />} />
          {/* D-265 — marketing funnel per campaign link (financial:review). */}
          <Route path="acquisition" element={<AcquisitionPage />} />
          <Route path="observability" element={<ObservabilityPage />} />
          <Route path="financial-reviews" element={<FinancialReviewsPage />} />
          <Route path="pricing" element={<DomainPricingPage />} />
          <Route path="tier-config" element={<TierConfigPage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </SpaRedirectLanding>
  );
}
